import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { getViewerTimezone, formatUtcNaiveInstantForViewer } from '../utils/dateTime';
import './ActivityLogPage.css';

// ── Role access ──────────────────────────────────────────────────────────────
const ALLOWED_ROLES = ['AGENT', 'TRANSACTION_COORDINATOR', 'BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'];

// Scope label per role group
const SCOPE_LABEL = {
  AGENT: 'Showing logs for transactions you are part of only',
  TRANSACTION_COORDINATOR: 'Showing logs for transactions you are assigned to only',
  BROKER: 'Showing logs for all agents under your brokerage',
  BROKER_MANAGER: 'Showing logs for all agents under your brokerage',
  BROKER_ADMIN: 'Showing logs for all agents under your brokerage',
};

// ── Activity type → badge label (matches mobile app exactly) ─────────────────
const TYPE_BADGE_LABEL = {
  TRANSACTION_CREATED: 'CREATE',
  TRANSACTION_UPDATED: 'UPDATE',
  STATUS_CHANGED: 'UPDATE',
  PARTICIPANT_ADDED: 'ROLE ASSIGNED',
  PARTICIPANT_UPDATED: 'UPDATE',
  PARTICIPANT_REMOVED: 'DELETE',
  MILESTONE_CREATED: 'CREATE',
  MILESTONE_UPDATED: 'UPDATE',
  MILESTONE_COMPLETED: 'UPDATE',
  CONTINGENCY_ADDED: 'CREATE',
  CONTINGENCY_UPDATED: 'UPDATE',
  CONTINGENCY_SATISFIED: 'UPDATE',
  CONTINGENCY_WAIVED: 'UPDATE',
  CONTINGENCY_REMOVED: 'DELETE',
  TASK_CREATED: 'CREATE',
  TASK_UPDATED: 'UPDATE',
  TASK_COMPLETED: 'UPDATE',
  TASK_CANCELLED: 'DELETE',
  DOCUMENT_LINKED: 'CREATE',
  DOCUMENT_UPLOADED: 'CREATE',
  DOCUMENT_SIGNED: 'UPDATE',
  NOTE_ADDED: 'CREATE',
  PRICE_CHANGED: 'UPDATE',
  DATE_CHANGED: 'UPDATE',
  CLOSING_COMPLETED: 'UPDATE',
};

// Category → list of raw API types (for client-side filtering)
const CATEGORY_TYPES = {
  CREATE: Object.keys(TYPE_BADGE_LABEL).filter(k => TYPE_BADGE_LABEL[k] === 'CREATE'),
  UPDATE: Object.keys(TYPE_BADGE_LABEL).filter(k => TYPE_BADGE_LABEL[k] === 'UPDATE'),
  DELETE: Object.keys(TYPE_BADGE_LABEL).filter(k => TYPE_BADGE_LABEL[k] === 'DELETE'),
  'ROLE ASSIGNED': ['PARTICIPANT_ADDED'],
};

const ENTITY_LABEL = {
  TRANSACTION: 'Transaction',
  PARTICIPANT: 'Party',
  MILESTONE: 'Milestone',
  CONTINGENCY: 'Contingency',
  TASK: 'Task',
  DOCUMENT: 'Document',
  NOTE: 'Note',
};

// Category filter pills (matches mobile app)
const FILTER_PILLS = [
  { key: null, label: 'All' },
  { key: 'CREATE', label: 'CREATE' },
  { key: 'UPDATE', label: 'UPDATE' },
  { key: 'DELETE', label: 'DELETE' },
  { key: 'ROLE ASSIGNED', label: 'ROLE' },
];

const RESOURCE_OPTIONS = [
  { value: '', label: 'All resources' },
  { value: 'TRANSACTION', label: 'Transaction' },
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'PARTICIPANT', label: 'Party' },
  { value: 'MILESTONE', label: 'Milestone' },
  { value: 'TASK', label: 'Task' },
];

const PAGE_SIZE = 20;

// ── Helpers ──────────────────────────────────────────────────────────────────

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function defaultFromDate() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return toISODate(d);
}

function defaultToDate() {
  return toISODate(new Date());
}

// entry.createdAt is a bare backend LocalDateTime (UTC digits, no 'Z') —
// render it in the viewer's own profile-state timezone, not the device's.
function formatTimestamp(dateStr) {
  if (!dateStr) return '—';
  const zone = getViewerTimezone();
  const { dateLabel, timeLabel } = formatUtcNaiveInstantForViewer(dateStr, zone);
  if (!dateLabel) return '—';
  const isoWithZone = /[Zz]|[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : `${dateStr}Z`;
  const isToday = new Date(isoWithZone).toLocaleDateString('en-CA', { timeZone: zone }) === new Date().toLocaleDateString('en-CA', { timeZone: zone });
  if (isToday) return `Today · ${timeLabel}`;
  return `${dateLabel} · ${timeLabel}`;
}

function formatLastActivity(dateStr) {
  if (!dateStr) return { value: '—', sub: '' };
  const zone = getViewerTimezone();
  const { dateLabel, timeLabel } = formatUtcNaiveInstantForViewer(dateStr, zone);
  if (!dateLabel) return { value: '—', sub: '' };
  const isoWithZone = /[Zz]|[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : `${dateStr}Z`;
  const date = new Date(isoWithZone);
  const dayKey = (d) => d.toLocaleDateString('en-CA', { timeZone: zone });
  const todayKey = dayKey(new Date());
  if (dayKey(date) === todayKey) return { value: 'Today', sub: timeLabel };
  if (dayKey(new Date(Date.now() - 86400000)) === dayKey(date)) return { value: 'Yesterday', sub: timeLabel };
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
  return { value: `${diffDays}d ago`, sub: timeLabel };
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name[0].toUpperCase();
}

function cleanActionText(text) {
  if (!text) return '—';
  // Strip raw UIDs (long alphanumeric tokens without spaces, 20+ chars)
  return text.replace(/\b[a-zA-Z0-9]{20,}\b/g, '').replace(/\s{2,}/g, ' ').trim() || text;
}

// Extract role tag and context detail from entry newValue/previousValue (mirrors mobile logic)
function extractContextInfo(entry) {
  const nv = entry.newValue;
  const pv = entry.previousValue;
  let contextDetail = '';
  let roleTag = '';

  if (entry.entityType === 'PARTICIPANT' && nv) {
    roleTag = nv.role || '';
  } else if (entry.entityType === 'MILESTONE' && nv) {
    contextDetail = nv.milestoneName || '';
  } else if (entry.entityType === 'CONTINGENCY' && nv) {
    contextDetail = nv.contingencyName || '';
  } else if (entry.entityType === 'TASK' && nv) {
    contextDetail = nv.taskTitle || '';
  } else if (entry.entityType === 'DOCUMENT' && nv) {
    contextDetail = nv.documentName || nv.fileName || '';
  } else if (entry.type === 'STATUS_CHANGED' && pv && nv) {
    contextDetail = `${pv.status || ''} → ${nv.status || ''}`;
  }

  return { roleTag, contextDetail };
}

// ── Component ─────────────────────────────────────────────────────────────────

const ActivityLogPage = () => {
  const navigate = useNavigate();

  // Role check
  const currentRole = useMemo(() => getActiveRoleFromStorage(), []);
  const hasAccess = ALLOWED_ROLES.includes(currentRole);

  useEffect(() => {
    if (!hasAccess) {
      navigate('/dashboard', { replace: true });
    }
  }, [hasAccess, navigate]);

  const [currentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  });

  // Stats & transactions state
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);

  // Log list state
  const [logs, setLogs] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Filters ──
  // Auto-applied immediately
  const [selectedCategory, setSelectedCategory] = useState(null); // null = All
  const [selectedTransaction, setSelectedTransaction] = useState('');
  const [selectedResource, setSelectedResource] = useState('');
  // Applied only via Apply button
  const [filterFrom, setFilterFrom] = useState(defaultFromDate);
  const [filterTo, setFilterTo]   = useState(defaultToDate);
  const [appliedFrom, setAppliedFrom] = useState(defaultFromDate);
  const [appliedTo, setAppliedTo]   = useState(defaultToDate);

  const getFirebaseToken = useCallback(() => new Promise((resolve, reject) => {
    if (auth.currentUser) { auth.currentUser.getIdToken(true).then(resolve).catch(reject); return; }
    const unsub = auth.onAuthStateChanged(user => {
      unsub();
      if (user) user.getIdToken(true).then(resolve).catch(reject);
      else reject(new Error('Not authenticated'));
    });
    setTimeout(() => reject(new Error('Auth timeout')), 8000);
  }), []);

  // ── Fetch stats (re-runs when applied dates or transaction filter changes) ─
  useEffect(() => {
    if (!hasAccess) return;
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const token = await getFirebaseToken();
        const params = { role: currentRole };
        if (appliedFrom) params.startDate = appliedFrom;
        if (appliedTo)   params.endDate   = appliedTo;
        if (selectedTransaction) params.transactionId = selectedTransaction;
        const res = await apiClient.get(API_ENDPOINTS.activityLogStats, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
        setStats(res.data);
      } catch (err) {
        console.error('Activity log stats error:', err);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, [hasAccess, getFirebaseToken, currentRole, appliedFrom, appliedTo, selectedTransaction]);

  // ── Fetch accessible transactions (once on mount) ────────────────────────
  useEffect(() => {
    if (!hasAccess) return;
    const fetchTransactions = async () => {
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(API_ENDPOINTS.activityLogTransactions, {
          headers: { Authorization: `Bearer ${token}` },
          params: { role: currentRole },
        });
        setTransactions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Activity log transactions error:', err);
      }
    };
    fetchTransactions();
  }, [hasAccess, getFirebaseToken, currentRole]);

  // ── Fetch logs — rebuilds when any applied filter changes ────────────────
  const fetchLogs = useCallback(async (page = 0) => {
    if (!hasAccess) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getFirebaseToken();
      const params = { page, size: PAGE_SIZE, role: currentRole };
      if (selectedTransaction)  params.transactionId = selectedTransaction;
      if (appliedFrom)          params.startDate = appliedFrom;
      if (appliedTo)            params.endDate   = appliedTo;
      if (selectedResource)     params.entityType = selectedResource;

      // For category filter: pass first matching raw type to API, client-filter for the rest
      if (selectedCategory && CATEGORY_TYPES[selectedCategory]?.length) {
        params.type = CATEGORY_TYPES[selectedCategory][0];
      }

      const res = await apiClient.get(API_ENDPOINTS.activityLogs, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const data = res.data;
      let content = Array.isArray(data.content) ? data.content : [];

      // Client-side filter when category maps to multiple types
      if (selectedCategory && CATEGORY_TYPES[selectedCategory]?.length > 1) {
        const allowed = new Set(CATEGORY_TYPES[selectedCategory]);
        content = content.filter(e => allowed.has(e.type));
      }

      setLogs(content);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(data.page || 0);
    } catch (err) {
      console.error('Activity log fetch error:', err);
      setError('Failed to load activity log. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [hasAccess, getFirebaseToken, currentRole, selectedTransaction, selectedCategory, selectedResource, appliedFrom, appliedTo]);

  // Auto-fetch on filter/date changes
  useEffect(() => {
    fetchLogs(0);
  }, [fetchLogs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track previous category/txn to auto-apply immediately (mirrors mobile useEffect)
  const prevCategory = useRef(selectedCategory);
  const prevTransaction = useRef(selectedTransaction);
  const prevResource = useRef(selectedResource);
  useEffect(() => {
    const catChanged = prevCategory.current !== selectedCategory;
    const txnChanged = prevTransaction.current !== selectedTransaction;
    const resChanged = prevResource.current !== selectedResource;
    prevCategory.current = selectedCategory;
    prevTransaction.current = selectedTransaction;
    prevResource.current = selectedResource;
    if (catChanged || txnChanged || resChanged) fetchLogs(0);
  }, [selectedCategory, selectedTransaction, selectedResource]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyDates = () => {
    setAppliedFrom(filterFrom);
    setAppliedTo(filterTo);
  };

  // appliedFrom/appliedTo change triggers fetchLogs via the main useEffect

  const handlePageChange = (page) => fetchLogs(page);

  // ── Export CSV ────────────────────────────────────────────────────────────
  const handleExportCSV = async () => {
    try {
      const token = await getFirebaseToken();
      const params = { page: 0, size: 10000, role: currentRole };
      if (selectedTransaction)  params.transactionId = selectedTransaction;
      if (appliedFrom)          params.startDate = appliedFrom;
      if (appliedTo)            params.endDate   = appliedTo;
      if (selectedResource)     params.entityType = selectedResource;
      if (selectedCategory && CATEGORY_TYPES[selectedCategory]?.[0]) {
        params.type = CATEGORY_TYPES[selectedCategory][0];
      }
      const res = await apiClient.get(API_ENDPOINTS.activityLogs, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      let rows = Array.isArray(res.data.content) ? res.data.content : [];
      if (selectedCategory && CATEGORY_TYPES[selectedCategory]?.length > 1) {
        const allowed = new Set(CATEGORY_TYPES[selectedCategory]);
        rows = rows.filter(e => allowed.has(e.type));
      }
      const header = ['Timestamp', 'User', 'Action', 'Resource', 'Transaction', 'Address', 'Description'];
      const csvRows = [
        header.join(','),
        ...rows.map(r => [
          `"${formatTimestamp(r.createdAt)}"`,
          `"${r.userName || ''}"`,
          `"${TYPE_BADGE_LABEL[r.type] || r.type || ''}"`,
          `"${ENTITY_LABEL[r.entityType] || r.entityType || ''}"`,
          `"${r.transactionName || ''}"`,
          `"${r.propertyAddress || ''}"`,
          `"${cleanActionText(r.action)}"`,
        ].join(','))
      ];
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `activity-log-${defaultToDate()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const isMe = (entry) => {
    if (!currentUser) return false;
    return entry.userId === currentUser.id ||
           entry.userId === currentUser.userId ||
           entry.userId === currentUser.firebaseUid;
  };

  const txnDropdownItems = useMemo(() => {
    return transactions.map(t => ({
      value: t.id,
      label: t.propertyAddress ? `${t.name} — ${t.propertyAddress}` : t.name,
    }));
  }, [transactions]);

  const lastActivity = formatLastActivity(stats?.lastActivityAt ?? null);
  const scopeLabel   = SCOPE_LABEL[currentRole] || 'Showing logs for your transactions only';

  // ── Pagination renderer ───────────────────────────────────────────────────
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const start = Math.max(0, currentPage - 2);
    const end   = Math.min(totalPages - 1, currentPage + 2);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return (
      <div className="alp-pagination">
        <div className="alp-pg-info">
          Showing {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, totalElements)} of {totalElements}
        </div>
        <div className="alp-pg-btns">
          <button className="alp-pg-btn" disabled={currentPage === 0} onClick={() => handlePageChange(currentPage - 1)}>‹</button>
          {pages.map(p => (
            <button key={p} className={`alp-pg-btn${p === currentPage ? ' alp-pg-btn--active' : ''}`} onClick={() => handlePageChange(p)}>{p + 1}</button>
          ))}
          <button className="alp-pg-btn" disabled={currentPage >= totalPages - 1} onClick={() => handlePageChange(currentPage + 1)}>›</button>
        </div>
      </div>
    );
  };

  if (!hasAccess) return null;

  return (
    <div className="alp-page">
      <div className="alp-main-content">
        <div className="alp-content-box">
          <div className="alp-content">

            {/* ── Page Header ── */}
            <div className="alp-page-header">
              <button className="alp-back-btn" onClick={() => navigate(-1)}>
                <span>←</span>
                <span>Back</span>
              </button>
              <div className="alp-header-row">
                <div>
                  <h1 className="alp-page-title">Activity Log</h1>
                  <p className="alp-page-subtitle">Actions across your transactions</p>
                </div>
                <button className="alp-export-btn" onClick={handleExportCSV}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Export CSV
                </button>
              </div>
              <div className="alp-scope-note">{scopeLabel}</div>
            </div>

            {/* ── Stats ── */}
            <div className="alp-stats">
              <div className="alp-stat">
                <div className="alp-stat-label">My Transactions</div>
                <div className="alp-stat-val">
                  {statsLoading ? <span className="alp-skeleton-val" /> : (stats?.activeTransactionCount ?? '—')}
                </div>
                <div className="alp-stat-sub">Active</div>
              </div>
              <div className="alp-stat">
                <div className="alp-stat-label">Total Actions</div>
                <div className="alp-stat-val">
                  {statsLoading ? <span className="alp-skeleton-val" /> : (stats?.totalActions ?? '—')}
                </div>
                <div className="alp-stat-sub">Last 30 days</div>
              </div>
              <div className="alp-stat">
                <div className="alp-stat-label">Last Activity</div>
                <div className="alp-stat-val alp-stat-val--sm">
                  {statsLoading ? <span className="alp-skeleton-val alp-skeleton-val--sm" /> : lastActivity.value}
                </div>
                <div className="alp-stat-sub">{statsLoading ? '' : lastActivity.sub}</div>
              </div>
            </div>

            {/* ── Category Pills (auto-apply) ── */}
            <div className="alp-pills-row">
              {FILTER_PILLS.map(pill => (
                <button
                  key={pill.key ?? 'all'}
                  className={`alp-pill${selectedCategory === pill.key ? ' alp-pill--active' : ''}`}
                  onClick={() => setSelectedCategory(pill.key)}
                >{pill.label}</button>
              ))}
            </div>

            {/* ── Filters ── */}
            <div className="alp-filters">
              <div className="alp-filter-group alp-filter-group--wide">
                <label className="alp-filter-label">Transaction</label>
                <select
                  className="alp-filter-select"
                  value={selectedTransaction}
                  onChange={e => setSelectedTransaction(e.target.value)}
                >
                  <option value="">All my transactions</option>
                  {txnDropdownItems.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="alp-filter-group">
                <label className="alp-filter-label">Resource</label>
                <select
                  className="alp-filter-select"
                  value={selectedResource}
                  onChange={e => setSelectedResource(e.target.value)}
                >
                  {RESOURCE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="alp-filter-group">
                <label className="alp-filter-label">From</label>
                <input className="alp-filter-input" type="date" value={filterFrom} max={filterTo || defaultToDate()} onChange={e => setFilterFrom(e.target.value)} />
              </div>
              <div className="alp-filter-group">
                <label className="alp-filter-label">To</label>
                <input className="alp-filter-input" type="date" value={filterTo} min={filterFrom} max={defaultToDate()} onChange={e => setFilterTo(e.target.value)} />
              </div>
              <button className="alp-apply-btn" onClick={handleApplyDates}>Apply</button>
            </div>

            {/* ── Table ── */}
            <div className="alp-table-wrap">
              {error && (
                <div className="alp-error">
                  {error}
                  <button className="alp-retry-btn" onClick={() => fetchLogs(currentPage)}>Retry</button>
                </div>
              )}
              <table className="alp-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Resource</th>
                    <th>Transaction</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 6 }).map((_, j) => (
                          <td key={j}><span className="alp-cell-skeleton" /></td>
                        ))}
                      </tr>
                    ))
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="alp-empty">
                        <div className="alp-empty-icon">
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#C4BDF0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                            <line x1="7" y1="8" x2="17" y2="8"/>
                            <line x1="7" y1="12" x2="17" y2="12"/>
                            <line x1="7" y1="16" x2="13" y2="16"/>
                          </svg>
                        </div>
                        <div className="alp-empty-text">No activity found</div>
                        <div className="alp-empty-sub">
                          {selectedCategory || selectedTransaction || selectedResource
                            ? 'Try adjusting your filters'
                            : 'Transaction activity will appear here'}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    logs.map(entry => {
                      const badgeLabel = TYPE_BADGE_LABEL[entry.type] || 'OTHER';
                      const badgeClass = badgeLabel.toLowerCase().replace(/\s+/g, '-');
                      const entityLabel = ENTITY_LABEL[entry.entityType] || entry.entityType || '—';
                      const me = isMe(entry);
                      const initials = getInitials(entry.userName);
                      const subtitle = entry.propertyAddress || entry.transactionName || '—';
                      const actionText = cleanActionText(entry.action);
                      const { roleTag, contextDetail } = extractContextInfo(entry);
                      return (
                        <tr key={entry.id}>
                          <td className="alp-td-ts">{formatTimestamp(entry.createdAt)}</td>
                          <td>
                            <div className="alp-user-cell">
                              <div className={`alp-avatar${me ? ' alp-avatar--me' : ''}`}>{initials}</div>
                              <div className="alp-user-info">
                                <span className="alp-user-name">{entry.userName || '—'}</span>
                                {me && <span className="alp-me-tag">Me</span>}
                              </div>
                            </div>
                          </td>
                          <td><span className={`alp-badge alp-badge--${badgeClass}`}>{badgeLabel}</span></td>
                          <td><span className="alp-resource-tag">{entityLabel}</span></td>
                          <td className="alp-td-txn" title={subtitle}>{subtitle}</td>
                          <td className="alp-td-desc">
                            <div className="alp-desc-text">{actionText}</div>
                            {(roleTag || contextDetail) && (
                              <div className="alp-context-tags">
                                {roleTag && <span className="alp-context-tag">{roleTag}</span>}
                                {contextDetail && <span className="alp-context-tag">{contextDetail}</span>}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              {!loading && logs.length > 0 && renderPagination()}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogPage;
